import { zodResolver } from '@hookform/resolvers/zod';
import {
  apiClient,
  getApiErrorMessage,
  getIntegrationMatterGetAllSensorsQueryKey,
  getIntegrationMatterGetAllServersQueryKey,
  useIntegrationMatterCreateSensor,
  useIntegrationMatterCreateServer,
  useIntegrationMatterDeleteSensor,
  useIntegrationMatterDeleteServer,
  useIntegrationMatterGetAllSensors,
  useIntegrationMatterGetAllServers,
  useIntegrationMatterPauseServer,
  useIntegrationMatterResumeServer,
  useIntegrationMatterUpdateSensor,
  useIntegrationMatterUpdateServer,
  type MatterMatterSensor,
  type MatterMatterServer,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Pause,
  Pencil,
  Play,
  Plus,
  Server,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, Select, SlideOutPanel, TextField } from '@/components/ui';

type MatterAttribute = {
  id: number;
  name?: string;
  value?: unknown;
};

type MatterCluster = {
  id: number;
  name?: string;
  attributes?: MatterAttribute[];
};

type MatterEndpoint = {
  id: number;
  clusters?: MatterCluster[];
};

type MatterDeviceType = {
  deviceType: number;
  revision: number;
};

type MatterHardwareNode = {
  serverId: string;
  nodeId: number;
  available?: boolean;
  isBridge?: boolean;
  vendorName?: string;
  productName?: string;
  nodeLabel?: string;
  vendorId?: number;
  productId?: number;
  serialNumber?: string;
  hardwareVersion?: number;
  softwareVersion?: number;
  softwareVersionString?: string;
  manufacturingDate?: string;
  uniqueId?: string;
  dateCommissioned?: string;
  lastInterview?: string;
  interviewVersion?: number;
  deviceTypes?: MatterDeviceType[];
  endpoints?: MatterEndpoint[];
};

type MatterHardwareResponse = {
  hardware?: MatterHardwareNode[];
};

const matterHardwareQueryKey = ['integrationMatterHardware'];

const fetchMatterHardware = async (): Promise<MatterHardwareResponse> => {
  const { data } = await apiClient.get<MatterHardwareResponse>('/api/integrations/matter/hardware');
  return data;
};

const clusterMappings: Record<number, { name: string; attributes: Record<number, string> }> = {
  6: { name: 'OnOff', attributes: { 0: 'OnOff' } },
  8: { name: 'LevelControl', attributes: { 0: 'CurrentLevel' } },
  1026: { name: 'TemperatureMeasurement', attributes: { 0: 'MeasuredValue' } },
  1029: { name: 'RelativeHumidityMeasurement', attributes: { 0: 'MeasuredValue' } },
};

const getClusterName = (clusterId: number, providedName?: string) => {
  if (providedName?.trim()) return providedName;
  return clusterMappings[clusterId]?.name ?? `Cluster ${clusterId}`;
};

const getAttributeName = (clusterId: number, attributeId: number, providedName?: string) => {
  if (providedName?.trim()) return providedName;
  return clusterMappings[clusterId]?.attributes?.[attributeId] ?? `Attribute ${attributeId}`;
};

const formatAttributeValue = (value: unknown) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return Number.isFinite(value) ? `${value}` : '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  return 'Unsupported';
};

const getNumericValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return { value: undefined as number | undefined };
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { value };
  }
  if (typeof value === 'boolean') {
    return { value: value ? 1 : 0, hint: 'Boolean values map to 0/1.' };
  }
  return { value: undefined as number | undefined, error: 'Current value is not numeric.' };
};

const applyTransform = (value: number | undefined, transform: string) => {
  if (!transform.trim() || value === undefined) {
    return { value: undefined as number | undefined };
  }
  try {
    // Matches backend expression syntax (govaluate) for simple arithmetic previews.
    const result = new Function('value', `return ${transform};`)(value) as unknown;
    if (typeof result !== 'number' || !Number.isFinite(result)) {
      return {
        value: undefined as number | undefined,
        error: 'Transform did not return a number.',
      };
    }
    return { value: result };
  } catch {
    return { value: undefined as number | undefined, error: 'Unable to evaluate transform.' };
  }
};

const formatNodeDate = (value?: string) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return undefined;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(parsed);
};

const formatDeviceTypes = (deviceTypes?: MatterDeviceType[]) => {
  if (!deviceTypes || deviceTypes.length === 0) return undefined;
  return deviceTypes.map((device) => `${device.deviceType} (rev ${device.revision})`).join(', ');
};

const getNodeDisplayName = (node: MatterHardwareNode) => {
  if (node.nodeLabel?.trim()) return node.nodeLabel;
  if (node.productName?.trim()) return node.productName;
  if (node.vendorName?.trim()) return `${node.vendorName} device`;
  return `Node ${node.nodeId}`;
};

const getNodeOptionLabel = (node: MatterHardwareNode) => {
  const displayName = getNodeDisplayName(node);
  if (displayName === `Node ${node.nodeId}`) return displayName;
  return `${displayName} (Node ${node.nodeId})`;
};

type Tab = 'servers' | 'sensors';

export function MatterSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('servers');

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Matter Integration
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            Manage Matter servers, hardware, and sensors
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-white/[0.06]">
        <nav className="-mb-px flex gap-6">
          {(['servers', 'sensors'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'servers' && <ServersTab />}
      {activeTab === 'sensors' && <SensorsTab />}
    </div>
  );
}

// ============================================================================
// SERVERS TAB
// ============================================================================

const createServerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  websocketUrl: z.string().min(1, 'WebSocket URL is required').max(500),
});

const updateServerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255).optional(),
  websocketUrl: z.string().min(1, 'WebSocket URL is required').max(500).optional(),
});

type CreateServerFormData = z.infer<typeof createServerSchema>;
type UpdateServerFormData = z.infer<typeof updateServerSchema>;

function ServersTab() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useIntegrationMatterGetAllServers();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingServer, setEditingServer] = useState<MatterMatterServer | null>(null);
  const [deletingServer, setDeletingServer] = useState<MatterMatterServer | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const createMutation = useIntegrationMatterCreateServer();
  const updateMutation = useIntegrationMatterUpdateServer();
  const deleteMutation = useIntegrationMatterDeleteServer();
  const pauseMutation = useIntegrationMatterPauseServer();
  const resumeMutation = useIntegrationMatterResumeServer();

  const servers = data?.servers ?? [];

  const invalidateServers = () => {
    queryClient.invalidateQueries({ queryKey: getIntegrationMatterGetAllServersQueryKey() });
  };

  const handlePauseResume = async (server: MatterMatterServer) => {
    if (!server.id) return;
    try {
      if (server.connection?.state === 'paused') {
        await resumeMutation.mutateAsync({ id: server.id });
      } else {
        await pauseMutation.mutateAsync({ id: server.id });
      }
      invalidateServers();
    } catch {
      // Error handling done by mutation
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Servers
        </h2>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Add Server
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch servers</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your Matter servers.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {servers.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Server className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No servers configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add your first Matter server to get started
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add Server
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {servers.map((server, index) => (
                <ServerListItem
                  key={server.id}
                  server={server}
                  isFirst={index === 0}
                  onEdit={() => setEditingServer(server)}
                  onDelete={() => setDeletingServer(server)}
                  onPauseResume={() => handlePauseResume(server)}
                  isPauseLoading={
                    (pauseMutation.isPending || resumeMutation.isPending) &&
                    (pauseMutation.variables?.id === server.id ||
                      resumeMutation.variables?.id === server.id)
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      <AddServerPanel
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            await createMutation.mutateAsync({ data });
            setShowAddModal(false);
            invalidateServers();
          } catch (err) {
            const message = getApiErrorMessage(err, 'Failed to add server.');
            setAddError(message);
          }
        }}
        isLoading={createMutation.isPending}
        error={addError}
      />

      {editingServer && (
        <EditServerPanel
          isOpen={true}
          server={editingServer}
          onClose={() => {
            setEditingServer(null);
            setEditError(null);
          }}
          onSubmit={async (data) => {
            if (!editingServer.id) return;
            setEditError(null);
            try {
              await updateMutation.mutateAsync({ id: editingServer.id, data });
              setEditingServer(null);
              invalidateServers();
            } catch (err) {
              const message = getApiErrorMessage(err, 'Failed to update server.');
              setEditError(message);
            }
          }}
          isLoading={updateMutation.isPending}
          error={editError}
        />
      )}

      {deletingServer && (
        <DeleteServerPanel
          isOpen={true}
          server={deletingServer}
          onClose={() => setDeletingServer(null)}
          onConfirm={async () => {
            if (!deletingServer.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingServer.id });
              setDeletingServer(null);
              invalidateServers();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

interface ServerListItemProps {
  server: MatterMatterServer;
  isFirst: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPauseResume: () => void;
  isPauseLoading: boolean;
}

function ServerListItem({
  server,
  isFirst,
  onEdit,
  onDelete,
  onPauseResume,
  isPauseLoading,
}: ServerListItemProps) {
  const connectionState = server.connection?.state;

  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-emerald-400 shadow-lg shadow-emerald-400/30';
      case 'connecting':
        return 'bg-amber-400 shadow-lg shadow-amber-400/30';
      case 'paused':
        return 'bg-gray-400 dark:bg-zinc-600';
      case 'not-connected':
        return 'bg-gray-400 dark:bg-zinc-600';
      default:
        return 'bg-gray-400 dark:bg-zinc-600';
    }
  };

  const getStatusText = () => {
    switch (connectionState) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'paused':
        return 'Paused';
      case 'not-connected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  };

  const isPaused = connectionState === 'paused';

  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-4 p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`mt-1 h-3 w-3 rounded-full ${getStatusColor()}`} />
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">{server.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span>{server.websocketUrl}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{getStatusText()}</span>
          </div>
          {server.connection?.lastError && (
            <div className="mt-1 text-xs text-red-500">{server.connection.lastError}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPauseResume}
          disabled={isPauseLoading}
          title={isPaused ? 'Resume connection' : 'Pause connection'}
        >
          {isPauseLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          ) : isPaused ? (
            <Play className="h-4 w-4" />
          ) : (
            <Pause className="h-4 w-4" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

interface AddServerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateServerFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function AddServerPanel({ isOpen, onClose, onSubmit, isLoading, error }: AddServerPanelProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateServerFormData>({
    resolver: zodResolver(createServerSchema),
    defaultValues: {
      name: '',
      websocketUrl: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-matter-server-form" loading={isLoading}>
        Add Server
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Matter Server"
      subtitle="Connect to a new Matter server"
      size="md"
      footer={footerContent}
    >
      <form id="add-matter-server-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Home Matter Server"
          error={errors.name?.message}
          {...register('name')}
        />
        <TextField
          label="WebSocket URL"
          placeholder="ws://localhost:5580/ws"
          error={errors.websocketUrl?.message}
          {...register('websocketUrl')}
        />
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </SlideOutPanel>
  );
}

interface EditServerPanelProps {
  isOpen: boolean;
  server: MatterMatterServer;
  onClose: () => void;
  onSubmit: (data: UpdateServerFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function EditServerPanel({
  isOpen,
  server,
  onClose,
  onSubmit,
  isLoading,
  error,
}: EditServerPanelProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateServerFormData>({
    resolver: zodResolver(updateServerSchema),
    defaultValues: {
      name: server.name ?? '',
      websocketUrl: server.websocketUrl ?? '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-matter-server-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Matter Server"
      subtitle="Update server connection details"
      size="md"
      footer={footerContent}
    >
      <form id="edit-matter-server-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Home Matter Server"
          error={errors.name?.message}
          {...register('name')}
        />
        <TextField
          label="WebSocket URL"
          placeholder="ws://localhost:5580/ws"
          error={errors.websocketUrl?.message}
          {...register('websocketUrl')}
        />
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </SlideOutPanel>
  );
}

interface DeleteServerPanelProps {
  isOpen: boolean;
  server: MatterMatterServer;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteServerPanel({
  isOpen,
  server,
  onClose,
  onConfirm,
  isLoading,
}: DeleteServerPanelProps) {
  const footerContent = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete Server
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Matter Server"
      subtitle="This will remove the server and stop its connections."
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete <span className="font-medium">{server.name}</span>?
      </p>
    </SlideOutPanel>
  );
}

// ============================================================================
// SENSORS TAB
// ============================================================================

function SensorsTab() {
  const queryClient = useQueryClient();
  const { data: serversData } = useIntegrationMatterGetAllServers();
  const { data, isLoading, isError, refetch } = useIntegrationMatterGetAllSensors();

  const [showSensorPanel, setShowSensorPanel] = useState(false);
  const [editingSensor, setEditingSensor] = useState<MatterMatterSensor | null>(null);
  const [deletingSensor, setDeletingSensor] = useState<MatterMatterSensor | null>(null);

  const deleteMutation = useIntegrationMatterDeleteSensor();

  const sensors = data?.sensors ?? [];
  const servers = serversData?.servers ?? [];
  const hasServers = servers.length > 0;

  const getServerName = (serverId?: string) => {
    const server = servers.find((item) => item.id === serverId);
    return server?.name ?? 'Unknown Server';
  };

  const invalidateSensors = () => {
    queryClient.invalidateQueries({ queryKey: getIntegrationMatterGetAllSensorsQueryKey() });
  };

  const handleClosePanel = () => {
    setShowSensorPanel(false);
    setEditingSensor(null);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Sensors
        </h2>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowSensorPanel(true)}
          disabled={!hasServers}
          title={!hasServers ? 'Add a Matter server first' : undefined}
        >
          Add Sensor
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch sensors</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading Matter sensors.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {sensors.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Activity className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No sensors configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                {hasServers
                  ? 'Create a sensor to start reporting Matter attribute values'
                  : 'Add a Matter server, then create sensors'}
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowSensorPanel(true)}
                disabled={!hasServers}
              >
                Add Sensor
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              <div className="grid grid-cols-1 gap-4 border-b border-gray-200 px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400 dark:border-white/[0.06] dark:text-zinc-500 sm:grid-cols-2 lg:grid-cols-4">
                <div>Name</div>
                <div>Human ID</div>
                <div>Server</div>
                <div>Attribute Path</div>
              </div>
              {sensors.map((sensor, index) => (
                <SensorRow
                  key={sensor.id}
                  sensor={sensor}
                  serverName={getServerName(sensor.serverId)}
                  isFirst={index === 0}
                  onEdit={() => setEditingSensor(sensor)}
                  onDelete={() => setDeletingSensor(sensor)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <MatterSensorPanel
        isOpen={showSensorPanel || !!editingSensor}
        onClose={handleClosePanel}
        sensor={editingSensor}
        servers={servers}
        onSuccess={invalidateSensors}
      />

      {deletingSensor && (
        <DeleteSensorModal
          isOpen={true}
          sensor={deletingSensor}
          onClose={() => setDeletingSensor(null)}
          onConfirm={async () => {
            if (!deletingSensor.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingSensor.id });
              setDeletingSensor(null);
              invalidateSensors();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

interface SensorRowProps {
  sensor: MatterMatterSensor;
  serverName: string;
  isFirst: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function SensorRow({ sensor, serverName, isFirst, onEdit, onDelete }: SensorRowProps) {
  return (
    <div
      className={`grid grid-cols-1 gap-4 px-4 py-3 text-sm text-gray-600 dark:text-zinc-400 sm:grid-cols-2 lg:grid-cols-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-gray-900 dark:text-white">{sensor.name}</span>
        <div className="flex items-center gap-2 sm:hidden">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
      <div className="text-xs font-mono text-gray-500 dark:text-zinc-500 sm:text-sm">
        {sensor.humanId}
      </div>
      <div className="text-xs text-gray-500 dark:text-zinc-500 sm:text-sm">{serverName}</div>
      <div className="flex items-center justify-between gap-3">
        <div className="break-all text-xs font-mono text-gray-500 dark:text-zinc-500 sm:text-sm">
          {sensor.nodeId}/{sensor.endpointId}/{sensor.clusterId}/{sensor.attributeId}
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface DeleteSensorModalProps {
  isOpen: boolean;
  sensor: MatterMatterSensor;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteSensorModal({
  isOpen,
  sensor,
  onClose,
  onConfirm,
  isLoading,
}: DeleteSensorModalProps) {
  const footerContent = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete Sensor
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Matter Sensor"
      subtitle="This will stop reporting values for this sensor."
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete <span className="font-medium">{sensor.name}</span>?
      </p>
    </SlideOutPanel>
  );
}

// ============================================================================
// SENSOR PANEL
// ============================================================================

const sensorFormSchema = z.object({
  humanId: z.string().min(1, 'Human ID is required').max(255),
  name: z.string().min(1, 'Name is required').max(255),
  serverId: z.string().min(1, 'Server is required'),
  nodeId: z.string().regex(/^\d+$/, 'Select a device'),
  endpointId: z.string().regex(/^\d+$/, 'Select an endpoint'),
  clusterId: z.string().regex(/^\d+$/, 'Select a cluster'),
  attributeId: z.string().regex(/^\d+$/, 'Select an attribute'),
  transform: z.string().max(1000).optional(),
});

type SensorFormData = z.infer<typeof sensorFormSchema>;

interface MatterSensorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sensor?: MatterMatterSensor | null;
  servers: MatterMatterServer[];
  onSuccess?: () => void;
}

function MatterSensorPanel({
  isOpen,
  onClose,
  sensor,
  servers,
  onSuccess,
}: MatterSensorPanelProps) {
  const queryClient = useQueryClient();
  const { data: hardwareData } = useQuery({
    queryKey: matterHardwareQueryKey,
    queryFn: fetchMatterHardware,
  });
  const createMutation = useIntegrationMatterCreateSensor();
  const updateMutation = useIntegrationMatterUpdateSensor();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!sensor;

  const title = isEditing ? 'Edit Sensor' : 'Add Sensor';
  const subtitle = isEditing ? 'Update sensor configuration' : 'Configure a new Matter sensor';

  const initialValues = useMemo(() => {
    if (sensor) {
      return {
        humanId: sensor.humanId,
        name: sensor.name,
        serverId: sensor.serverId ?? '',
        nodeId: sensor.nodeId !== undefined ? `${sensor.nodeId}` : '',
        endpointId: sensor.endpointId !== undefined ? `${sensor.endpointId}` : '',
        clusterId: sensor.clusterId !== undefined ? `${sensor.clusterId}` : '',
        attributeId: sensor.attributeId !== undefined ? `${sensor.attributeId}` : '',
        transform: sensor.transform ?? '',
      };
    }

    return {
      humanId: '',
      name: '',
      serverId: '',
      nodeId: '',
      endpointId: '',
      clusterId: '',
      attributeId: '',
      transform: '',
    };
  }, [sensor]);

  const handleSubmit = async (data: SensorFormData) => {
    setError(null);
    try {
      if (isEditing && sensor?.id) {
        await updateMutation.mutateAsync({
          id: sensor.id,
          data: {
            name: data.name,
            transform: data.transform || undefined,
          },
        });
      } else {
        await createMutation.mutateAsync({
          data: {
            humanId: data.humanId,
            name: data.name,
            serverId: data.serverId,
            nodeId: Number(data.nodeId),
            endpointId: Number(data.endpointId),
            clusterId: Number(data.clusterId),
            attributeId: Number(data.attributeId),
            transform: data.transform || undefined,
          },
        });
      }

      queryClient.invalidateQueries({ queryKey: getIntegrationMatterGetAllSensorsQueryKey() });
      onSuccess?.();
      onClose();
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        isEditing ? 'Failed to update sensor.' : 'Failed to create sensor.'
      );
      setError(message);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="matter-sensor-form" loading={isLoading}>
        {isEditing ? 'Save Changes' : 'Create Sensor'}
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="lg"
      footer={footer}
    >
      <MatterSensorForm
        formId="matter-sensor-form"
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        isEditing={isEditing}
        servers={servers}
        hardware={hardwareData?.hardware ?? []}
      />
    </SlideOutPanel>
  );
}

interface MatterSensorFormProps {
  formId: string;
  initialValues: Partial<SensorFormData>;
  onSubmit: (data: SensorFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  isEditing?: boolean;
  servers: MatterMatterServer[];
  hardware: MatterHardwareNode[];
}

function MatterSensorForm({
  formId,
  initialValues,
  onSubmit,
  isLoading: _isLoading,
  error,
  isEditing,
  servers,
  hardware,
}: MatterSensorFormProps) {
  const lockPathFields = !!isEditing;
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<SensorFormData>({
    resolver: zodResolver(sensorFormSchema),
    defaultValues: {
      humanId: initialValues.humanId ?? '',
      name: initialValues.name ?? '',
      serverId: initialValues.serverId ?? '',
      nodeId: initialValues.nodeId ?? '',
      endpointId: initialValues.endpointId ?? '',
      clusterId: initialValues.clusterId ?? '',
      attributeId: initialValues.attributeId ?? '',
      transform: initialValues.transform ?? '',
    },
  });

  const serverOptions = useMemo(
    () =>
      servers
        .filter((server) => server.id)
        .map((server) => ({
          value: server.id ?? '',
          label: server.name ?? 'Unknown Server',
        })),
    [servers]
  );

  const selectedServerId = useWatch({ control, name: 'serverId' }) ?? '';
  const selectedNodeId = useWatch({ control, name: 'nodeId' }) ?? '';
  const selectedEndpointId = useWatch({ control, name: 'endpointId' }) ?? '';
  const selectedClusterId = useWatch({ control, name: 'clusterId' }) ?? '';
  const selectedAttributeId = useWatch({ control, name: 'attributeId' }) ?? '';
  const transform = useWatch({ control, name: 'transform' }) ?? '';

  const hardwareOptions = useMemo(
    () =>
      hardware
        .filter((node) => node.serverId && node.serverId === selectedServerId)
        .map((node) => {
          const commissioned = formatNodeDate(node.dateCommissioned);
          return {
            value: `${node.nodeId}`,
            label: getNodeOptionLabel(node),
            description: [
              [node.vendorName, node.productName].filter(Boolean).join(' '),
              formatDeviceTypes(node.deviceTypes),
              node.available === false ? 'Offline' : 'Online',
              commissioned ? `Commissioned ${commissioned}` : '',
            ]
              .filter(Boolean)
              .join(' • '),
          };
        }),
    [hardware, selectedServerId]
  );

  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return undefined;
    const nodeId = Number(selectedNodeId);
    if (!selectedServerId || !Number.isFinite(nodeId)) return undefined;
    return hardware.find((node) => node.serverId === selectedServerId && node.nodeId === nodeId);
  }, [hardware, selectedNodeId, selectedServerId]);

  const endpointOptions = useMemo(() => {
    return (selectedNode?.endpoints ?? [])
      .filter((endpoint) => endpoint.id !== 0)
      .map((endpoint) => ({
        value: `${endpoint.id}`,
        label: `Endpoint ${endpoint.id}`,
      }));
  }, [selectedNode]);

  const selectedEndpoint = useMemo(() => {
    if (!selectedEndpointId) return undefined;
    const endpointId = Number(selectedEndpointId);
    if (!Number.isFinite(endpointId)) return undefined;
    return (selectedNode?.endpoints ?? [])
      .filter((endpoint) => endpoint.id !== 0)
      .find((endpoint) => endpoint.id === endpointId);
  }, [selectedNode, selectedEndpointId]);

  const clusterOptions = useMemo(() => {
    return (selectedEndpoint?.clusters ?? []).map((cluster) => ({
      value: `${cluster.id}`,
      label: getClusterName(cluster.id, cluster.name),
      description: `#${cluster.id}`,
    }));
  }, [selectedEndpoint]);

  const selectedCluster = useMemo(() => {
    if (!selectedClusterId) return undefined;
    const clusterId = Number(selectedClusterId);
    if (!Number.isFinite(clusterId)) return undefined;
    return (selectedEndpoint?.clusters ?? []).find((cluster) => cluster.id === clusterId);
  }, [selectedEndpoint, selectedClusterId]);

  const attributeOptions = useMemo(() => {
    if (!selectedCluster) return [];
    const attributes = selectedCluster.attributes ?? [];
    return attributes.map((attribute) => ({
      value: `${attribute.id}`,
      label: getAttributeName(selectedCluster.id, attribute.id, attribute.name),
      description: `Value: ${formatAttributeValue(attribute.value)}`,
    }));
  }, [selectedCluster]);

  const selectedAttribute = useMemo(() => {
    if (!selectedAttributeId) return undefined;
    const attributeId = Number(selectedAttributeId);
    if (!Number.isFinite(attributeId)) return undefined;
    return (selectedCluster?.attributes ?? []).find((attribute) => attribute.id === attributeId);
  }, [selectedAttributeId, selectedCluster]);

  const currentValueState = getNumericValue(selectedAttribute?.value);
  const transformState = applyTransform(currentValueState.value, transform);
  const endpointSelectDisabled = lockPathFields || endpointOptions.length <= 1;
  const commissioned = selectedNode ? formatNodeDate(selectedNode.dateCommissioned) : undefined;
  const lastInterview = selectedNode ? formatNodeDate(selectedNode.lastInterview) : undefined;
  const vendorProduct = selectedNode
    ? [selectedNode.vendorName, selectedNode.productName].filter(Boolean).join(' ')
    : '';

  useEffect(() => {
    if (lockPathFields || serverOptions.length !== 1 || selectedServerId) return;
    const firstOption = serverOptions[0];
    if (!firstOption) return;
    setValue('serverId', firstOption.value, { shouldValidate: true });
  }, [lockPathFields, selectedServerId, serverOptions, setValue]);

  useEffect(() => {
    if (lockPathFields || hardwareOptions.length === 0 || selectedNodeId) return;
    if (hardwareOptions.length > 1) return;
    const firstOption = hardwareOptions[0];
    if (!firstOption) return;
    setValue('nodeId', firstOption.value, { shouldValidate: true });
  }, [hardwareOptions, lockPathFields, selectedNodeId, setValue]);

  useEffect(() => {
    if (lockPathFields || endpointOptions.length === 0 || selectedEndpointId) return;
    if (endpointOptions.length > 1) return;
    const firstOption = endpointOptions[0];
    if (!firstOption) return;
    setValue('endpointId', firstOption.value, { shouldValidate: true });
  }, [endpointOptions, lockPathFields, selectedEndpointId, setValue]);

  useEffect(() => {
    if (lockPathFields || clusterOptions.length === 0 || selectedClusterId) return;
    const firstOption = clusterOptions[0];
    if (!firstOption) return;
    setValue('clusterId', firstOption.value, { shouldValidate: true });
  }, [clusterOptions, lockPathFields, selectedClusterId, setValue]);

  useEffect(() => {
    if (lockPathFields || attributeOptions.length === 0 || selectedAttributeId) return;
    const firstOption = attributeOptions[0];
    if (!firstOption) return;
    setValue('attributeId', firstOption.value, { shouldValidate: true });
  }, [attributeOptions, lockPathFields, selectedAttributeId, setValue]);

  const handleServerChange = (value: string) => {
    setValue('serverId', value ?? '', { shouldValidate: true });
    setValue('nodeId', '', { shouldValidate: true });
    setValue('endpointId', '', { shouldValidate: true });
    setValue('clusterId', '', { shouldValidate: true });
    setValue('attributeId', '', { shouldValidate: true });
  };

  const handleDeviceChange = (value: string) => {
    setValue('nodeId', value ?? '', { shouldValidate: true });
    setValue('endpointId', '', { shouldValidate: true });
    setValue('clusterId', '', { shouldValidate: true });
    setValue('attributeId', '', { shouldValidate: true });
  };

  const handleEndpointChange = (value: string) => {
    setValue('endpointId', value, { shouldValidate: true });
    setValue('clusterId', '', { shouldValidate: true });
    setValue('attributeId', '', { shouldValidate: true });
  };

  const handleClusterChange = (value: string) => {
    setValue('clusterId', value, { shouldValidate: true });
    setValue('attributeId', '', { shouldValidate: true });
  };

  const handleAttributeChange = (value: string) => {
    setValue('attributeId', value, { shouldValidate: true });
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Human ID"
          placeholder="living-room-temperature"
          error={errors.humanId?.message}
          hint={isEditing ? 'Human ID cannot be changed after creation.' : undefined}
          disabled={!!isEditing}
          {...register('humanId')}
        />
        <TextField
          label="Name"
          placeholder="Living Room Temperature"
          error={errors.name?.message}
          {...register('name')}
        />
      </div>

      <div className="space-y-3">
        <Controller
          name="serverId"
          control={control}
          render={() => (
            <Select
              label="Server"
              value={selectedServerId}
              onChange={handleServerChange}
              options={serverOptions}
              error={errors.serverId?.message}
              disabled={lockPathFields || serverOptions.length === 0}
              placeholder="Select a server"
            />
          )}
        />
        <Controller
          name="nodeId"
          control={control}
          render={() => (
            <Select
              label="Device"
              value={selectedNodeId}
              onChange={handleDeviceChange}
              options={hardwareOptions}
              error={errors.nodeId?.message}
              disabled={lockPathFields || hardwareOptions.length === 0 || !selectedServerId}
              placeholder={selectedServerId ? 'Select a device' : 'Select a server first'}
            />
          )}
        />
        {selectedNode && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-zinc-400">
            <div className="mb-1 font-medium text-gray-700 dark:text-zinc-300">
              {getNodeDisplayName(selectedNode)}
            </div>
            <div className="grid gap-1">
              <div>Node ID: {selectedNode.nodeId}</div>
              <div>Status: {selectedNode.available === false ? 'Offline' : 'Online'}</div>
              {vendorProduct && <div>{vendorProduct}</div>}
              {formatDeviceTypes(selectedNode.deviceTypes) && (
                <div>Device types: {formatDeviceTypes(selectedNode.deviceTypes)}</div>
              )}
              {commissioned && <div>Commissioned: {commissioned}</div>}
              {lastInterview && <div>Last interview: {lastInterview}</div>}
              {selectedNode.isBridge && <div>Bridge: yes</div>}
            </div>
          </div>
        )}
        <Controller
          name="endpointId"
          control={control}
          render={() => (
            <Select
              label="Endpoint"
              value={selectedEndpointId}
              onChange={handleEndpointChange}
              options={endpointOptions}
              error={errors.endpointId?.message}
              disabled={endpointSelectDisabled}
              placeholder={
                endpointOptions.length === 1 ? 'Endpoint auto-selected' : 'Select an endpoint'
              }
            />
          )}
        />
        <Controller
          name="clusterId"
          control={control}
          render={() => (
            <Select
              label="Cluster"
              value={selectedClusterId}
              onChange={handleClusterChange}
              options={clusterOptions}
              error={errors.clusterId?.message}
              disabled={lockPathFields || clusterOptions.length === 0}
              placeholder="Select a cluster"
            />
          )}
        />
        <Controller
          name="attributeId"
          control={control}
          render={() => (
            <Select
              label="Attribute"
              value={selectedAttributeId}
              onChange={handleAttributeChange}
              options={attributeOptions}
              error={errors.attributeId?.message}
              disabled={lockPathFields || attributeOptions.length === 0}
              placeholder="Select an attribute"
            />
          )}
        />
      </div>

      <TextField
        label="Transform (govaluate)"
        placeholder="value / 10"
        error={errors.transform?.message}
        hint="Use value in your expression, e.g. value / 10"
        {...register('transform')}
      />

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-900">
        <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Preview
        </div>
        <div className="mt-2 grid gap-2 text-sm text-gray-700 dark:text-zinc-300">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-500 dark:text-zinc-500">Current value</span>
            <span className="font-medium">
              {selectedAttribute ? formatAttributeValue(selectedAttribute.value) : '—'}
            </span>
          </div>
          {currentValueState.hint && (
            <div className="text-xs text-amber-500">{currentValueState.hint}</div>
          )}
          {currentValueState.error && (
            <div className="text-xs text-red-500">{currentValueState.error}</div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-500 dark:text-zinc-500">Transformed</span>
            <span className="font-medium">
              {transform.trim()
                ? transformState.value !== undefined
                  ? transformState.value
                  : '—'
                : formatAttributeValue(selectedAttribute?.value)}
            </span>
          </div>
          {transformState.error && (
            <div className="text-xs text-red-500">{transformState.error}</div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          {error}
        </div>
      )}
    </form>
  );
}
